import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import apiClient from "../api/apiClient";
import { message } from "antd";
import { uploadImageToCloudinary } from "../helpers/cloudinary";

export interface Product {
  id: number;
  category_id: number;
  location_id: number;
  vendor_id: number;
  name: string;
  description: string;
  qty: number;
  status: boolean;
  amount: number;
  slug: string;
  discount: number;
  created_at: string;
  updated_at: string;
  deleted_at?: string | null;

  category: {
    id: number;
    name: string;
  };

  location: {
    id: number;
    name: string;
    address: string;
    code: string;
  };

  vendor: {
    id: number;
    name: string;
    email: string;
    phone: string;
    avatar?: string;
  };

  images?: {
    id: number;
    product_id: number;
    url: string;
  }[];
}

// ✅ Fetch All Products with Pagination Support
export const useProducts = (
  page: number,
  filters: Record<string, any> = {}
) => {
  return useQuery({
    queryKey: ["products", page, filters],
    queryFn: async () => {
      const queryParams = new URLSearchParams({
        page: page.toString(),
        ...Object.fromEntries(
          Object.entries(filters).filter(
            ([_, v]) =>
              v !== null &&
              v !== "" &&
              v !== undefined &&
              !(Array.isArray(v) && v.length === 0)
          )
        ), // Exclude null/empty filters
      }).toString();

      const response = await apiClient.get<{
        data: Product[];
        meta: { total: number; current_page: number; last_page: number };
      }>(`/product?${queryParams}`);

      return response.data;
    },
    staleTime: 1000 * 60 * 60, // Cache for 1 hour
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });
};

// ✅ Fetch Single Product Details
export const useProductDetails = (productId: number) => {
  return useQuery({
    queryKey: ["productDetails", productId],
    queryFn: async () => {
      const response = await apiClient.get<{ data: Product }>(
        `/product/${productId}`
      );
      return response.data.data;
    },
    staleTime: 1000 * 60 * 10, // ✅ Cache for 10 minutes
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    enabled: !!productId, // ✅ Prevents fetching if productId is undefined
  });
};

// ✅ Create a New Product
export const useCreateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      category_id: number;
      location_id: number[]; // Updated to array of location IDs
      vendor_id: number;
      name: string;
      description: string;
      qty: number;
      status: boolean;
      amount: number;
      slug: string;
      discount: number;
      imageFile: File | null; // Add imageFile to the payload
    }) => {
      let imageUrl = "";

      // Upload the image if a new file is provided
      if (data.imageFile) {
        try {
          imageUrl = await uploadImageToCloudinary(data.imageFile); // Upload to Cloudinary and get the URL
        } catch (error) {
          console.error("Failed to upload image:", error);
          throw new Error("Failed to upload image. Please try again.");
        }
      }

      const payload = {
        category_id: data.category_id,
        location_id: data.location_id, // Send the array of location IDs
        vendor_id: data.vendor_id,
        name: data.name,
        description: data.description,
        qty: data.qty,
        status: data.status,
        amount: data.amount,
        slug: data.slug,
        discount: data.discount,
        images: imageUrl ? [{ url: imageUrl }] : [], // Include the Cloudinary URL
      };

      console.log("Creating product with data:", payload); // Debugging

      const response = await apiClient.post<{ data: Product }>(
        "/product/create",
        payload
      );

      return response.data.data;
    },
    onSuccess: () => {
      message.success("Product created successfully!");
      queryClient.invalidateQueries({ queryKey: ["products"] });
    },
    onError: () => {
      message.error("Failed to create product. Please try again.");
    },
  });
};

// ✅ Update an Existing Product
export const useUpdateProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      id: number; // Product ID
      category_id: number;
      location_id: number[]; // Updated to array of location IDs
      vendor_id: number;
      name: string;
      description: string;
      qty: number;
      status: number | boolean;
      amount: number;
      slug: string;
      discount: number;
      imageFile: File | null;
    }) => {
      try {
        // Save the original ID for later use
        const productId = data.id;

        // 1. Extract imageFile and id from data
        const { imageFile, id, ...productData } = data;

        // 2. Update the product details (with empty images array)
        const response = await apiClient.put<{ data: Product }>(
          `/product/${id}`,
          {
            ...productData,
            images: [], // Always send empty array
          }
        );

        let productResponse = response.data.data;

        // 3. If a new image is provided, handle it separately
        if (imageFile) {
          try {
            // Upload to Cloudinary
            const imageUrl = await uploadImageToCloudinary(imageFile);

            // Get current product details to check existing images
            const currentProductResponse = await apiClient.get<{
              data: Product;
            }>(`/product/${id}`);
            const existingImages =
              currentProductResponse.data.data.images || [];

            if (existingImages.length > 0) {
              // Update the existing image
              await apiClient.put(`/productimage/${existingImages[0].id}`, {
                product_id: id,
                url: imageUrl,
              });

              // Update the product response with the new image
              productResponse = {
                ...productResponse,
                images: [{ ...existingImages[0], url: imageUrl }],
              };
            } else {
              // Create a new image
              const newImageResponse = await apiClient.post(
                `/productimage/create`,
                {
                  product_id: id,
                  url: imageUrl,
                }
              );

              // Update the product response with the new image
              productResponse = {
                ...productResponse,
                images: [newImageResponse.data.data],
              };
            }
          } catch (error) {
            console.error("Image update error:", error);
            // Don't throw error here, just log it and continue
            message.warning(
              "Product updated but image update failed. You can try again later."
            );
          }
        }

        // Ensure we return a proper productResponse with the original ID
        return {
          ...productResponse,
          id: productId, // Make sure id is always included
        };
      } catch (error) {
        console.error("Product update error:", error);
        // Now properly throw the error for the mutation to catch
        throw error;
      }
    },
    onSuccess: (data) => {
      // Safety check - if data exists and has an id property
      if (data && data.id) {
        message.success("Product updated successfully!");
        queryClient.invalidateQueries({ queryKey: ["products"] });
        queryClient.invalidateQueries({
          queryKey: ["productDetails", data.id],
        });
      } else {
        // If data is missing id, still show success but only invalidate the products list
        message.success("Product updated successfully!");
        queryClient.invalidateQueries({ queryKey: ["products"] });
        // Log this unusual condition for debugging
        console.warn("Product updated but returned data missing id:", data);
      }
    },
    onError: (error) => {
      console.error("Mutation error:", error);
      message.error("Failed to update product. Please try again.");
    },
  });
};

export const useDeleteProduct = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (productId: number) => {
      const response = await apiClient.delete(`/product/${productId}`);
      return response.data;
    },
    onSuccess: () => {
      message.success("Product deleted successfully!");
      queryClient.invalidateQueries({ queryKey: ["products"] }); // Refresh the product list
    },
    onError: () => {
      message.error("Failed to delete product. Please try again.");
    },
  });
};
